import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { LineReveal } from "@/components/motion/Reveal";
import { PROFILE, IMAGES } from "@/data/content";

const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.3]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen w-full flex-col justify-end overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroTexture}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,51,51,0.14),transparent_55%)]" />
      </motion.div>

      {/* Top-left metadata */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute left-6 top-28 z-10 md:left-12 md:top-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="font-mono text-[12px] uppercase leading-relaxed tracking-[0.18em] text-ash"
        >
          <p className="text-chalk">{PROFILE.name}</p>
          <p>{PROFILE.role}</p>
          <p>{PROFILE.location}</p>
        </motion.div>
      </motion.div>

      {/* Headline */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-14 md:px-12 md:pb-24"
      >
        <h1
          className="font-display font-semibold uppercase leading-[0.82] tracking-tightest text-chalk"
          style={{ fontSize: "clamp(3rem, 13vw, 11.5rem)" }}
        >
          <LineReveal lines={[PROFILE.heroLines[0], PROFILE.heroLines[1]]} delay={0.35} />
          <span className="reveal-line">
            <motion.span
              className="block text-outline-signal"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + 2 * 0.12 }}
            >
              {PROFILE.heroLines[2]}
            </motion.span>
          </span>
        </h1>

        {/* Capability line */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-6 font-mono text-[12px] uppercase tracking-[0.16em] text-signal sm:text-[13px]"
        >
          {PROFILE.capabilityLine}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 flex flex-col items-start justify-between gap-8 border-t border-line pt-8 lg:flex-row lg:items-end"
        >
          <p className="max-w-xl text-[15px] leading-[1.65] text-ash md:text-base">
            {PROFILE.intro}
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <button
              data-testid="hero-cta"
              onClick={() => onNavigate?.("#work")}
              className="group inline-flex min-h-[52px] shrink-0 items-center justify-center gap-3 rounded-none border border-chalk px-7 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-chalk transition-colors duration-300 hover:bg-signal hover:border-signal hover:text-ink"
            >
              View Selected Work
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              href={PROFILE.resumeUrl}
              download
              data-testid="hero-resume"
              className="group inline-flex min-h-[52px] shrink-0 items-center justify-center gap-3 rounded-none border border-signal px-7 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-signal transition-colors duration-300 hover:bg-signal hover:text-ink"
            >
              <Download className="h-4 w-4" />
              Download Résumé
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-3 rounded-full border border-white/10 bg-black/40 px-4 py-2 backdrop-blur-sm md:right-12 md:flex"
      >
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-signal" />
        <span className="font-mono text-[12px] uppercase tracking-[0.18em] text-chalk">{PROFILE.status}</span>
      </motion.div>
    </section>
  );
};

export default Hero;
